import { Fragment, Suspense, lazy, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { guideBodyFor, guidePlan, resolveGuide, type GuideEntry, type GuideLang, type GuideLine, type GuideSection, type GuideViewer } from "@salon-pos/shared";
import { GUIDE_INDEX, GUIDE_LOAD } from "virtual:senvia-guide";
import { useAuth } from "../auth";
import { useI18n, type MessageKey } from "../i18n";
import { IS_NATIVE_BUILD } from "../native/platform";
import { useGuideSubValue } from "../lib/guideSub";
import "./guide.css";

/**
 * Feature guide (功能说明书): the 说明书 button in the header of every feature page → side panel (desktop) / bottom
 * sheet (phone) with what the page is for, how to use it, the key settings, FAQ / tips, the plan badge, the full
 * tutorial and "Ask Senvia Assistant". Content: docs/help/guide/<id>.md. Store app (App Store 3.1.1): no plan badge,
 * no tutorial / assistant links (those are web only).
 */
// literal env check so the store-app build drops the chunk (no /api/ai/ in the native bundle)
const GuideArticle = import.meta.env.VITE_CLIENT === "native" ? null : lazy(() => import("./GuideArticle"));

const SEEN_KEY = (userId: string) => "spos.guide.seen." + userId;
function loadSeen(userId: string): string[] {
  try { const v = JSON.parse(localStorage.getItem(SEEN_KEY(userId)) ?? "[]"); return Array.isArray(v) ? v.filter((x) => typeof x === "string") : []; } catch { return []; }
}
function saveSeen(userId: string, ids: string[]) { try { localStorage.setItem(SEEN_KEY(userId), JSON.stringify(ids.slice(-300))); } catch { /* private mode */ } }

/** The guide id for the current page (path + the page's tab: reported by the page, else ?tab=). */
export function useCurrentGuide(): string | null {
  const loc = useLocation();
  const reported = useGuideSubValue();
  return useMemo(() => {
    const tab = new URLSearchParams(loc.search).get("tab");
    return resolveGuide(GUIDE_INDEX, loc.pathname, reported ?? tab);
  }, [loc.pathname, loc.search, reported]);
}

function bold(s: string): ReactNode[] {
  return s.split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith("**") && p.endsWith("**") && p.length > 4 ? <strong key={i}>{p.slice(2, -2)}</strong> : <Fragment key={i}>{p}</Fragment>));
}

function BookIcon() {
  return <svg className="guide-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /><path d="M9 7h7M9 11h5" /></svg>;
}

/**
 * `inDialog`: a second 说明书 button inside a blocking dialog (e.g. 开始营业 on the POS), whose dimmed overlay covers the
 * top-bar button. Same guide (current page), no first-visit hint; the panel (z-index 70) opens above the dialog overlay (50).
 */
export function GuideButton({ inDialog = false }: { inDialog?: boolean } = {}) {
  const { me } = useAuth();
  const { t } = useI18n();
  const id = useCurrentGuide();
  const loc = useLocation();
  const userId = me?.user.id ?? "";
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState<string[]>(() => (userId ? loadSeen(userId) : []));
  useEffect(() => { setSeen(userId ? loadSeen(userId) : []); }, [userId]);
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  const markSeen = useCallback((gid: string) => {
    setSeen((s) => { if (s.includes(gid)) return s; const n = [...s, gid]; if (userId) saveSeen(userId, n); return n; });
  }, [userId]);
  if (!me || !id) return null;
  const first = !inDialog && !seen.includes(id);
  const toggle = () => { markSeen(id); setOpen((o) => !o); };
  return (
    <div className={"guide-wrap" + (inDialog ? " guide-wrap-dialog" : "")}>
      <button type="button" className={"btn btn-soft btn-sm guide-btn" + (first ? " guide-pulse" : "")} onClick={toggle} aria-expanded={open} aria-haspopup="dialog" data-testid={inDialog ? "guide-button-dialog" : "guide-button"} data-guide={id} title={t("gd.buttonLong")}>
        <BookIcon /><span className="guide-btn-label">{t("gd.button")}</span>
      </button>
      {first && !open && (
        <div className="guide-hint" role="status" data-testid="guide-hint">
          <button type="button" className="guide-hint-text" onClick={toggle}>{t("gd.firstHint")}</button>
          <button type="button" className="guide-hint-x" aria-label={t("gd.dismiss")} onClick={() => markSeen(id)}>✕</button>
        </div>
      )}
      {open && <GuidePanel id={id} onClose={() => setOpen(false)} />}
    </div>
  );
}

const SECTION_LABEL: Record<GuideSection, MessageKey> = { what: "gd.what", steps: "gd.steps", settings: "gd.settings", tips: "gd.tips" };

export function GuidePanel({ id, onClose }: { id: string; onClose: () => void }) {
  const { me } = useAuth();
  const { t, locale } = useI18n();
  const [entry, setEntry] = useState<GuideEntry | null | "loading">("loading");
  const [full, setFull] = useState(false);
  useEffect(() => {
    let alive = true;
    setEntry("loading"); setFull(false);
    const load = GUIDE_LOAD[id];
    if (!load) { setEntry(null); return; }
    load().then((e) => { if (alive) setEntry(e); }, () => { if (alive) setEntry(null); });
    return () => { alive = false; };
  }, [id]);
  useEffect(() => {
    // stopPropagation: Escape closes only the guide, not a dialog underneath it (useDialog listens on window)
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);
  const lang: GuideLang = locale === "zh" ? "zh" : "en";
  const idx = GUIDE_INDEX.find((g) => g.id === id);
  const page = idx?.title[lang] || idx?.title.en || "";
  const viewer: GuideViewer = {
    countries: [(me?.tenant?.country ?? "").toUpperCase(), ...(me?.tenant?.taxRegimes ?? []).map((r) => r.toUpperCase())].filter(Boolean),
    native: IS_NATIVE_BUILD,
    manager: me?.membership?.role === "owner" || me?.membership?.role === "manager",
  };
  const body = entry && entry !== "loading" ? guideBodyFor(entry, lang, viewer) : null;
  const plan = guidePlan(idx?.plan ?? "base");
  const features = me?.features ?? [];
  const planOn: boolean | null = plan.kind === "addon" ? features.includes(plan.feature) : plan.kind === "full" ? fullOn(features, id) : null;
  const ask = () => {
    onClose();
    window.dispatchEvent(new CustomEvent("spos:support-open", { detail: { prefill: t("gd.askPrefill", { page }) } }));
  };
  const line = (l: GuideLine, i: number) => <li key={i}>{bold(l.text)}</li>;
  // portal: the top bar is a containing block for position:fixed (backdrop blur), so the panel renders under <body>
  return createPortal(
    <>
      <button type="button" className="guide-scrim" aria-label={t("gd.close")} onClick={onClose} />
      <aside className="guide-panel" role="dialog" aria-modal="false" aria-label={t("gd.title", { page })} data-testid="guide-panel" data-guide={id}>
        <div className="guide-grip" aria-hidden />
        <header className="guide-head">
          <div className="guide-head-text">
            <span className="guide-kicker"><BookIcon /> {t("gd.buttonLong")}</span>
            <h2>{page}</h2>
            {!IS_NATIVE_BUILD && (
              <div className="guide-badges">
                <span className={"guide-plan guide-plan-" + plan.kind} data-testid="guide-plan">{t(plan.kind === "base" ? "gd.plan.base" : plan.kind === "full" ? "gd.plan.full" : "gd.plan.addon")}</span>
                {planOn !== null && <span className={"guide-plan-state" + (planOn ? " on" : "")} data-testid="guide-plan-state">{t(planOn ? "gd.plan.on" : "gd.plan.off")}</span>}
              </div>
            )}
          </div>
          <button type="button" className="btn btn-quiet btn-sm guide-x" aria-label={t("gd.close")} onClick={onClose}>✕</button>
        </header>
        <div className="guide-body">
          {entry === "loading" && <p className="muted">{t("gd.loading")}</p>}
          {body && (["what", "steps", "settings", "tips"] as const).map((s) => body[s].length > 0 && (
            <section key={s} className={"guide-sec guide-sec-" + s} data-testid={"guide-sec-" + s}>
              <h3>{t(SECTION_LABEL[s])}</h3>
              {s === "what" ? body.what.map((l, i) => <p key={i}>{bold(l.text)}</p>) : s === "steps" ? <ol>{body.steps.map(line)}</ol> : <ul>{body[s].map(line)}</ul>}
            </section>
          ))}
          {full && GuideArticle && idx?.help && <Suspense fallback={<p className="muted small">{t("gd.fullLoading")}</p>}><GuideArticle id={idx.help} /></Suspense>}
        </div>
        {!IS_NATIVE_BUILD && (
          <footer className="guide-foot">
            {idx?.help && <button type="button" className="btn btn-soft btn-sm" onClick={() => setFull((f) => !f)} aria-expanded={full} data-testid="guide-full">{t(full ? "gd.fullHide" : "gd.full")}</button>}
            <button type="button" className="btn btn-primary btn-sm" onClick={ask} data-testid="guide-ask">💬 {t("gd.ask")}</button>
          </footer>
        )}
      </aside>
    </>,
    document.body,
  );
}

/** Full-plan-only pages: on when the shop has the feature behind the page (attendance / subscriptions / custom domain). */
function fullOn(features: string[], id: string): boolean {
  const f = /attendance|clock/.test(id) ? "attendance" : /subscription/.test(id) ? "subscriptions" : /domain/.test(id) ? "custom_domain" : null;
  return f ? features.includes(f) : true;
}
